/**
 * Header do chat com status online e animações
 */

import React from 'react';

interface HeaderProps {
  title: string;
  isConnected: boolean;
  isTyping: boolean;
  primaryColor: string;
  onClose: () => void;
  onMinimize?: () => void;
  // Novas props para avatar dinâmico
  headerAvatarMode?: 'logo' | 'agent';
  headerLogoUrl?: string | null;
  agentName?: string | null;
  agentAvatar?: string | null;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  isConnected,
  isTyping,
  primaryColor,
  onClose,
  onMinimize,
  // Novas props para avatar dinâmico
  headerAvatarMode = 'agent',
  headerLogoUrl,
  agentName,
  agentAvatar,
}) => {
  // Determina se deve exibir avatar do agente ou logo
  const showAgentAvatar = headerAvatarMode === 'agent' && agentName && agentAvatar;
  const avatarSrc = showAgentAvatar ? agentAvatar : headerLogoUrl;
  const displayName = showAgentAvatar ? agentName : title;

  // Estilos do header com gradiente sutil
  const headerStyles: React.CSSProperties = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, -15)} 100%)`,
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '16px 16px 0 0',
    position: 'relative',
    overflow: 'hidden',
  };

  // Estilos do efeito de brilho
  const shineStyles: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '200%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
    animation: 'headerShine 3s ease-in-out infinite',
  };

  // Estilos do avatar - fundo branco sólido para destacar a logo
  const avatarContainerStyles: React.CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.12)',
  };

  // Estilos da bolinha de status sobre o avatar
  const avatarStatusDotStyles: React.CSSProperties = {
    position: 'absolute',
    bottom: '0px',
    right: '0px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: isConnected ? '#22c55e' : '#f59e0b',
    border: '2px solid #ffffff',
    boxShadow: isConnected ? '0 0 6px #22c55e' : 'none',
  };

  const avatarImageStyles: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  // Estilos do título
  const titleStyles: React.CSSProperties = {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  };

  // Estilos do status (texto apenas, bolinha movida para avatar)
  const statusContainerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginTop: '2px',
  };

  // Mantido para compatibilidade, mas não será mais usado no layout
  const statusDotStyles: React.CSSProperties = {
    display: 'none', // Bolinha agora está sobre o avatar
  };

  // Estilos do texto de status
  const statusTextStyles: React.CSSProperties = {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.85)',
  };

  // Estilos do container de botões
  const buttonsContainerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  // Estilos do botão
  const buttonStyles: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.15)',
    border: 'none',
    borderRadius: '8px',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease, transform 0.2s ease',
  };

  // Função para ajustar cor (clarear/escurecer)
  function adjustColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (
      0x1000000 +
      (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 0 ? 0 : B) : 255)
    ).toString(16).slice(1);
  }

  // Determina texto de status
  const getStatusText = () => {
    if (isTyping) return 'Digitando...';
    if (isConnected) return 'Online agora';
    return 'Conectando...';
  };

  // Ícone padrão quando sem avatar/logo
  const DefaultAvatarIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  return (
    <>
      <style>
        {`
          @keyframes headerShine {
            0% { left: -100%; }
            50%, 100% { left: 100%; }
          }
          @keyframes statusPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
          }
          .uaizychat-header-btn:hover {
            background: rgba(255, 255, 255, 0.25) !important;
            transform: scale(1.05);
          }
          .uaizychat-header-btn:active {
            transform: scale(0.95);
          }
        `}
      </style>
      
      <div style={headerStyles}>
        <div style={shineStyles} />
        
        {/* Container com avatar + info */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
          {/* Avatar/Logo com bolinha de status */}
          <div style={{ position: 'relative', marginRight: '12px' }}>
            <div style={avatarContainerStyles}>
              {avatarSrc ? (
                <img 
                  src={avatarSrc} 
                  alt={displayName} 
                  style={avatarImageStyles}
                  onError={(e) => {
                    // Esconde imagem quebrada
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <DefaultAvatarIcon />
              )}
            </div>
            {/* Bolinha de status sobre o avatar */}
            <span style={avatarStatusDotStyles} />
          </div>
          
          {/* Nome e status */}
          <div>
            <h3 style={titleStyles}>{displayName}</h3>
            <div style={statusContainerStyles}>
              <span style={statusTextStyles}>{getStatusText()}</span>
            </div>
          </div>
        </div>
        
        <div style={buttonsContainerStyles}>
          {onMinimize && (
            <button
              onClick={onMinimize}
              style={buttonStyles}
              className="uaizychat-header-btn"
              aria-label="Minimizar chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}
          
          <button
            onClick={onClose}
            style={buttonStyles}
            className="uaizychat-header-btn"
            aria-label="Fechar chat"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

// Função auxiliar para ajustar cor (exportada para uso em outros componentes)
export function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 0 ? 0 : B) : 255)
  ).toString(16).slice(1);
}
